import React, { useEffect, useState } from 'react';
import { Customer } from '@/types';
import { useCustomerMutations } from '@features/admin/data';
import { X, Save, Loader2 } from 'lucide-react';
import { showAlertModal } from '@shared/components/common/AlertModal';
import { formatDobFromIso, formatDobInput, normalizeDobToIso } from '@/lib/formatters';
import { validateUkPhone } from '@/lib/validators';
import { normalizeErrorMessage } from '@/lib/errorUtils';

interface CustomerEditorProps {
  customer: Customer;
  onSaved: (updated: Customer) => void;
  onClose: () => void;
}

function formatDobForDisplay(value?: string): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const datePart = raw.includes('T') ? raw.split('T')[0] : raw;
  return formatDobFromIso(datePart);
}

function normalizeDobForSave(value?: string): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const datePart = raw.includes('T') ? raw.split('T')[0] : raw;
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
  return normalizeDobToIso(datePart.replace(/-/g, '/'));
}

export { formatDobForDisplay, normalizeDobForSave };

export const CustomerEditor: React.FC<CustomerEditorProps> = ({ customer, onSaved, onClose }) => {
  const customerMutations = useCustomerMutations();
  const [draft, setDraft] = useState<Customer>({
    ...customer,
    dob: formatDobForDisplay(customer.dob || ''),
    phone: customer.phone || ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);

  useEffect(() => {
    setPhoneTouched(false);
  }, [customer.id]);

  const phoneValue = String(draft.phone || '').trim();
  const showPhoneError = phoneTouched && phoneValue && !validateUkPhone(phoneValue).valid;

  const handleSave = async () => {
    const phoneResult = phoneValue ? validateUkPhone(phoneValue) : null;
    if (phoneResult && !phoneResult.valid) {
      setPhoneTouched(true);
      showAlertModal('Please enter a valid UK phone number.');
      return;
    }
    setIsSaving(true);
    try {
      const normalized: Customer = {
        ...draft,
        dob: normalizeDobForSave(draft.dob || ''),
        phone: phoneResult?.valid ? phoneResult.normalized : ''
      };
      await customerMutations.update(normalized);
      onSaved(normalized);
    } catch (err: unknown) {
      showAlertModal(`Error saving: ${normalizeErrorMessage(err, 'Unknown error')}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-up border border-slate-200">
        <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
          <h3 className="font-bold text-lg text-slate-800">Edit Patient Profile</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
              Full Name
            </label>
            <input
              className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                Date of Birth
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="DD-MM-YYYY"
                maxLength={10}
                className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
                value={draft.dob || ''}
                onChange={(e) => setDraft({ ...draft, dob: formatDobInput(e.target.value) })}
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                Job Title
              </label>
              <input
                className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
                value={draft.jobTitle || ''}
                onChange={(e) => setDraft({ ...draft, jobTitle: e.target.value })}
                placeholder="e.g. Crane Op"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
              Email Address
            </label>
            <input
              type="email"
              className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
              Mobile Phone
            </label>
            <input
              type="tel"
              className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
              value={draft.phone || ''}
              onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
              onBlur={(e) => {
                const value = e.target.value.trim();
                setPhoneTouched(true);
                const phoneResult = value ? validateUkPhone(value) : null;
                if (phoneResult?.valid) {
                  setDraft({ ...draft, phone: phoneResult.normalized });
                }
              }}
            />
            <p className="mt-1 text-[10px] text-slate-400">
              UK numbers only, e.g. 07123 456789 or +44 7123 456789.
            </p>
            {showPhoneError && (
              <p className="mt-1 text-xs font-semibold text-red-600">
                Enter a valid UK phone number.
              </p>
            )}
          </div>
        </div>
        <div className="p-6 border-t bg-slate-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 text-slate-500 font-bold hover:bg-white rounded-xl transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-teal-600 text-white px-8 py-2 rounded-xl font-bold hover:bg-teal-700 flex items-center gap-2 shadow-lg disabled:opacity-50"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Profile
          </button>
        </div>
      </div>
    </div>
  );
};
